import type { TPostAttachment } from '@onesource/schemas';
import {
  MAX_NON_VIDEO_ATTACHMENT_BYTES,
  defaultAttachmentLimits,
  formatBytes,
  maxAttachmentBytesFor,
} from '@onesource/schemas';
import { axiosServiceInstance } from '@onesource/services';

import { compressImageFile } from '../media/compress-image';
import { compressPdfFile } from '../media/compress-pdf';

export type TPostAttachmentType = TPostAttachment['type'];

/**
 * A single attachment in the composer / edit form. Covers both freshly picked
 * files (mid/just-uploaded, `file` set) and already-persisted attachments
 * seeded when editing a post (`file` undefined, `url` set).
 */
export interface PostAttachmentItem {
  id: string;
  // Object URL for a freshly picked file, or the remote URL for an existing one.
  preview: string;
  // Resolved remote URL — present once uploaded, or immediately for existing.
  url?: string;
  uploading: boolean;
  type: TPostAttachmentType;
  name?: string;
  mimeType?: string;
  size?: number;
  // Playback length, videos only — read from the file before upload.
  durationSeconds?: number;
  // Backing media_files row, present for compressible uploads (video / PDF).
  mediaId?: string;
}

// Posts accept only images, PDFs, and videos, capped per file.
export const MAX_ATTACHMENTS = 10;
export const ATTACHMENT_ACCEPT = 'image/*,application/pdf,video/*';
// Per-file ceilings live in schemas so the composer, the presign endpoint and
// the post guard cannot drift apart.
export { MAX_NON_VIDEO_ATTACHMENT_BYTES, defaultAttachmentLimits, formatBytes, maxAttachmentBytesFor };

/**
 * The optimised file is still over its ceiling.
 *
 * Distinct from a generic upload failure because it carries a message worth
 * showing verbatim: the ceiling that applies to an image or a PDF is lower than
 * the one the composer's hint advertises for videos, so "upload failed" alone
 * would leave the user with no idea what to change.
 */
export class AttachmentTooLargeError extends Error {}

export const detectAttachmentType = (file: File): TPostAttachmentType => {
  if (file.type.startsWith('image/')) {
    return 'image';
  }
  if (file.type.startsWith('video/')) {
    return 'video';
  }
  return 'file';
};

/**
 * Whether a picked file is an accepted *type*: images, PDFs and videos. Guards
 * against drag/paste bypassing the file input's `accept` filter. Size and video
 * length are checked separately, since those depend on the uploader's tier.
 */
export const isAcceptedAttachment = (file: File): boolean =>
  file.type.startsWith('image/') || file.type === 'application/pdf' || file.type.startsWith('video/');

/** Longest we wait for a browser to report video metadata before giving up. */
const VIDEO_METADATA_TIMEOUT_MS = 5000;

/** Longest we wait for an image to decode before assuming it is fine. */
const IMAGE_DECODE_TIMEOUT_MS = 10000;

/**
 * Whether the browser can actually decode this image.
 *
 * Catches a file that is the right size and mime type but not really an image —
 * a truncated download, a renamed file, a corrupt export. Nothing else in the
 * pipeline looks inside the bytes, so without this a 100MB of garbage uploads
 * happily and then renders as a broken tile forever.
 *
 * Only applied to images. The equivalent check on video would reject a
 * perfectly valid file whose codec this browser happens not to support (HEVC
 * off an iPhone is the common case), and corrupt videos already have a
 * downstream net: the transcoder fails them and the post is held.
 *
 * Fails OPEN on timeout — a large-but-valid image that decodes slowly must not
 * be mistaken for a corrupt one.
 */
export const isDecodableImage = (file: File): Promise<boolean> =>
  new Promise(resolve => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    let settled = false;

    const finish = (decodable: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      URL.revokeObjectURL(objectUrl);
      resolve(decodable);
    };

    const timer = setTimeout(() => finish(true), IMAGE_DECODE_TIMEOUT_MS);

    image.onload = () => finish(true);
    image.onerror = () => finish(false);
    image.src = objectUrl;
  });

/**
 * Playback length of a video file, read from its metadata without decoding the
 * stream. Resolves to `null` when the browser cannot determine it (an unusual
 * container, a corrupt file) — the value is advisory, so an unknown duration
 * lets the file through rather than blocking a pick on a browser quirk.
 */
export const readVideoDuration = (file: File): Promise<number | null> =>
  new Promise(resolve => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;

    const finish = (duration: number | null) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      URL.revokeObjectURL(objectUrl);
      video.removeAttribute('src');
      resolve(duration);
    };

    // A container the browser cannot decode (HEVC .mov off an iPhone, AV1, a
    // truncated file) may fire NEITHER loadedmetadata NOR error, leaving this
    // promise pending forever. The caller awaits it mid-pick, so a hang here
    // silently swallows the whole selection — no preview, no message, no
    // upload. Always settle.
    const timer = setTimeout(() => finish(null), VIDEO_METADATA_TIMEOUT_MS);

    video.preload = 'metadata';
    video.onloadedmetadata = () => finish(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => finish(null);
    video.src = objectUrl;
    // Detached elements do not always start fetching from `src` alone.
    video.load();
  });

/** The human-readable reason out of an axios error, if the API sent one. */
export const extractUploadErrorMessage = (error: unknown): string | undefined => {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (typeof message === 'string' && message) {
    return message;
  }
  return Array.isArray(message) && typeof message[0] === 'string' ? message[0] : undefined;
};

/**
 * Ask the server for an upload URL, declaring the file's size and (for video)
 * its length. The server refuses outright when either is over the caller's
 * ceiling, and signs the size into the URL so R2 enforces it.
 */
const getPresignedUrl = async (
  key: string,
  contentType: string,
  meta: { sizeBytes: number; durationSeconds?: number },
): Promise<{ uploadUrl: string; publicUrl: string | null }> => {
  const res = await axiosServiceInstance.get<{ uploadUrl: string; publicUrl: string | null }>('/upload/presigned-url', {
    params: {
      key,
      contentType,
      folder: 'community-hub-posts',
      sizeBytes: meta.sizeBytes,
      ...(meta.durationSeconds !== undefined && { durationSeconds: meta.durationSeconds }),
    },
  });
  return res.data;
};

/**
 * Strip the origin from an upload URL, keeping the path exactly as the object
 * key was written.
 *
 * Deliberately not `new URL(url).pathname`: that percent-encodes the path, and
 * the stored value is later used as the R2 object key by the transcoder and the
 * retention sweep. Re-encoding it at request time would double-encode
 * (`my video.mp4` → `my%2520video.mp4`) and miss the object. `pathname` also
 * truncates at a `#` in the filename, silently losing the extension.
 */
const toStoredPath = (url: string): string => {
  if (!url.startsWith('http')) {
    return url;
  }
  // slice() rather than pathname — preserves the raw key, including `#` and spaces.
  return url.slice(new URL(url).origin.length);
};

/**
 * Register the upload in `media_files` so async compression has a row to track.
 * Only videos still compress server-side (images are resized and PDFs are
 * compressed in the browser before upload), so everything else skips this
 * round-trip and attaches directly to its uploaded URL.
 *
 * Best-effort: a failure here costs compression, not the post, so the caller
 * still gets a usable attachment.
 */
const createMediaRecord = async (file: File, publicUrl: string): Promise<string | undefined> => {
  const isCompressible = file.type.startsWith('video/');
  if (!isCompressible) {
    return undefined;
  }

  try {
    const res = await axiosServiceInstance.post<{ id: string }>('/media', {
      originalName: file.name,
      // Stored as a path, matching what the media-manager writes.
      cdnUrls: { original: toStoredPath(publicUrl) },
      mimeType: file.type,
      sizeBytes: file.size,
    });
    return res.data.id;
  } catch {
    return undefined;
  }
};

/**
 * Optimise a picked file before it is uploaded.
 *
 * Images are downscaled and re-encoded, PDFs are compressed with Ghostscript —
 * both in the browser, so the object that reaches R2 is already the final one
 * and neither type needs an async transcode pass. Videos are untouched here;
 * they are the only type still compressed server-side.
 *
 * Both helpers fall back to the original file on any failure.
 */
const optimiseForUpload = (file: File): Promise<File> => {
  if (file.type === 'application/pdf') {
    return compressPdfFile(file);
  }
  return compressImageFile(file);
};

/**
 * Upload a file via the presigned-PUT R2 flow.
 *
 * Returns the public URL and the final file's metadata — compression can change
 * the size, name and type, and the attachment record must describe what was
 * actually stored — plus, for compressible types, the `media_files` id. Readers
 * use that id to serve the compressed derivative once the transcoder has
 * produced one.
 */
export const uploadPostFile = async (
  input: File,
  meta: { durationSeconds?: number; maxBytes?: number | null } = {},
): Promise<{ url: string; mediaId?: string; name: string; mimeType?: string; size: number }> => {
  const file = await optimiseForUpload(input);

  // Checked AFTER optimisation, on the bytes that will actually be stored: a
  // 20MB phone photo comes out of the resizer well under a megabyte, so
  // rejecting it on the picked size would refuse a file that is fine. The
  // presign endpoint applies the same ceiling to the same number.
  if (meta.maxBytes !== null && meta.maxBytes !== undefined && file.size > meta.maxBytes) {
    throw new AttachmentTooLargeError(
      `"${input.name}" is ${formatBytes(file.size)} after compression, over the ${formatBytes(meta.maxBytes)} limit.`,
    );
  }

  const key = `${Date.now()}-${file.name}`;
  const { uploadUrl, publicUrl } = await getPresignedUrl(key, file.type, {
    sizeBytes: file.size,
    durationSeconds: meta.durationSeconds,
  });

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  // Previously unchecked — a failed PUT silently produced an attachment
  // pointing at an object that was never written.
  if (!response.ok) {
    throw new Error(`Upload failed for ${file.name} (${response.status})`);
  }

  const url = publicUrl || uploadUrl.split('?')[0];
  // Videos get a media_files row the transcoder drives; other types skip it
  // inside createMediaRecord and attach directly to their uploaded URL.
  return {
    url,
    mediaId: await createMediaRecord(file, url),
    name: file.name,
    mimeType: file.type || undefined,
    size: file.size,
  };
};

/** Map a resolved item to the persisted attachment shape (null if not uploaded). */
export const itemToAttachment = (item: PostAttachmentItem): TPostAttachment | null => {
  if (!item.url) {
    return null;
  }
  return {
    url: item.url,
    type: item.type,
    name: item.name,
    mimeType: item.mimeType,
    size: item.size,
    durationSeconds: item.durationSeconds,
    mediaId: item.mediaId,
  };
};

/** Seed an item from an already-persisted attachment (for the edit form). */
export const attachmentToItem = (attachment: TPostAttachment): PostAttachmentItem => ({
  id: attachment.url,
  preview: attachment.url,
  url: attachment.url,
  uploading: false,
  type: attachment.type,
  name: attachment.name,
  mimeType: attachment.mimeType,
  size: attachment.size,
  durationSeconds: attachment.durationSeconds,
  mediaId: attachment.mediaId,
});
